/**
 * Axios HTTP 客户端封装
 * 
 * 功能：
 * - 统一 baseURL 配置
 * - 自动携带 Cookie 会话
 * - 统一错误处理与类型化错误
 * - 请求/响应拦截器
 */

import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios'
import type { ApiError } from '@/types'
import { handleMockRequest } from './mock'

/** 从 Cookie 中读取指定值（用于 CSRF 双提交校验） */
function getCookie(name: string): string | undefined {
  const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'))
  return match ? decodeURIComponent(match[1]) : undefined
}

/** API 基础路径（导出供 fetch 流式请求复用） */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'

/** 确保持有 CSRF Token：无 Cookie 时先向后端索取（导出供 fetch 流式请求复用） */
export async function ensureCsrfToken(baseURL: string = API_BASE_URL): Promise<string | undefined> {
  let token = getCookie('csrf_token')
  if (token) return token
  try {
    // 使用裸 axios 请求，避免经过实例拦截器造成递归
    await axios.get(`${baseURL}/auth/csrf-token`, { withCredentials: true })
    token = getCookie('csrf_token')
  } catch {
    // 索取失败时交由后端决定是否拒绝该请求
  }
  return token
}

/** 创建 API 客户端 */
function createApiClient(): AxiosInstance {
  const instance = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL || '/api/v1',
    timeout: 30000,
    withCredentials: true, // 携带 Cookie 会话
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    }
  })

  // 请求拦截器
  instance.interceptors.request.use(
    async (config) => {
      // 后端对非安全方法（POST/PUT/PATCH/DELETE）强制 CSRF 双提交校验
      if (import.meta.env.VITE_USE_MOCK !== 'true') {
        const method = (config.method || 'get').toUpperCase()
        if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
          const token = await ensureCsrfToken(config.baseURL || '')
          if (token) {
            config.headers.set('X-CSRF-Token', token)
          }
        }
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  // 响应拦截器
  instance.interceptors.response.use(
    (response) => response,
    (error: AxiosError<ApiError>) => {
      if (error.response) {
        const status = error.response.status
        const apiError = error.response.data

        // 统一错误处理
        if (status === 401) {
          // 未认证，触发全局事件
          window.dispatchEvent(new CustomEvent('auth:unauthorized'))
        }

        // 兼容两种后端错误格式：
        // {error: {code, message}}（全局异常处理器）与 {detail: {code, message}}（HTTPException）
        const errBody =
          apiError?.error ??
          (apiError && typeof apiError.detail === 'object' ? apiError.detail : null)
        const detailStr =
          apiError && typeof apiError.detail === 'string' ? apiError.detail : null

        // 返回类型化错误
        return Promise.reject({
          code: errBody?.code || `HTTP_${status}`,
          message: errBody?.message || detailStr || error.message,
          details: errBody?.details,
          status
        })
      }

      if (error.request) {
        return Promise.reject({
          code: 'NETWORK_ERROR',
          message: '网络连接失败，请检查网络设置',
          status: 0
        })
      }

      return Promise.reject({
        code: 'UNKNOWN_ERROR',
        message: error.message,
        status: 0
      })
    }
  )

  return instance
}

/** 是否启用 Mock 模式 */
const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'

/** 单例 API 客户端 */
export const apiClient = createApiClient()

/** Mock 拦截器 */
if (USE_MOCK) {
  apiClient.interceptors.request.use(async (config) => {
    // 拦截请求，返回 mock 数据
    const mockData = await handleMockRequest(config)
    // 创建一个模拟的 AxiosResponse
    return Promise.reject({
      __MOCK_DATA__: mockData,
      config,
      response: { data: mockData, status: 200, statusText: 'OK', headers: {}, config }
    })
  })

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      // 如果是 mock 数据，返回它
      if (error.__MOCK_DATA__ !== undefined) {
        return Promise.resolve({ data: error.__MOCK_DATA__, status: 200, statusText: 'OK', headers: {}, config: error.config })
      }
      return Promise.reject(error)
    }
  )
}

/** 类型化请求方法 */
export const api = {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.get(url, config).then((res) => res.data as T)
  },

  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.post(url, data, config).then((res) => res.data as T)
  },

  put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.put(url, data, config).then((res) => res.data as T)
  },

  patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.patch(url, data, config).then((res) => res.data as T)
  },

  delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return apiClient.delete(url, config).then((res) => res.data as T)
  }
}

/** 文件上传方法 */
export async function uploadFile<T>(
  url: string,
  file: File,
  onProgress?: (percent: number) => void
): Promise<T> {
  const formData = new FormData()
  formData.append('file', file)

  return apiClient
    .post(url, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (event.total && onProgress) {
          onProgress(Math.round((event.loaded * 100) / event.total))
        }
      }
    })
    .then((res) => res.data as T)
}

/** 文件下载方法（经 axios 读成 Blob，适合小文件；受 30s 超时与内存限制） */
export async function downloadFile(url: string, filename?: string): Promise<void> {
  const response = await apiClient.get(url, { responseType: 'blob' })
  const blob = new Blob([response.data])
  const downloadUrl = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename || 'download'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(downloadUrl)
}

/**
 * 触发浏览器原生下载（直接把同源 URL 交给浏览器下载管理器）
 *
 * 与 downloadFile 的区别：请求不由 axios 发起，因此
 * - 不受实例 30s 超时限制，也不把整份文件读进内存，几十至上百 MB 的文件照样能取；
 * - 能看到浏览器自带的下载进度/暂停/续传。
 * 适用于「登录后取回自己上传的文件」这类需要鉴权 Cookie 的同源接口。
 *
 * 同源 URL + download 属性会强制按附件保存，即便服务端返回错误响应也不会
 * 发生页面跳转、把 SPA 顶掉。
 */
export function triggerBrowserDownload(url: string, filename?: string): void {
  const link = document.createElement('a')
  link.href = url
  link.download = filename || 'download'
  link.rel = 'noopener'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}
